import produce from 'immer';

import {assert} from 'Asserts';
import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';

import {getFieldInfoFromDataFlowEdgeHandle} from '../flowchartIdentifierUtils';
import {convertRootRowToMapFromStartRow} from '../mapFromStartUtils';
import {
  FlowchartStaticEdgeData,
  ProcessFlowchartEdgeType,
  StaticTemplateFlowchart,
  TaskSchemaNodeSectionType,
} from '../processFlowchartConfigEditorConstants';
import {isStaticTaskSchemaNodeData, TaskSchemaStaticNodeData} from '../processFlowchartConfigEditorNodeTypes';
import {
  FlowchartEdgeChangeType,
  FlowchartNodeChange,
  FlowchartNodeChangeType,
} from '../processFlowchartConfigEditorUtils';
import getRouterConfigChangesAfterEdgeChange from './getRouterConfigChangesAfterEdgeChange';

function getNodeChangesForNewEdgeFromMapFromStartSection(
  staticFlowchart: StaticTemplateFlowchart,
  newEdge: FlowchartStaticEdgeData
): Array<FlowchartNodeChange> {
  if (newEdge.type !== ProcessFlowchartEdgeType.DATA_FLOW) {
    return [];
  }

  const sourceFieldInfo = getFieldInfoFromDataFlowEdgeHandle(newEdge.sourceHandle);
  if (sourceFieldInfo.sectionType !== TaskSchemaNodeSectionType.MAP_FROM_START) {
    return [];
  }

  const sourceNode = staticFlowchart.nodesStaticData.find((node) => node.id === newEdge.source);
  assert(sourceNode != null, 'Source node should not be null');
  assert(isStaticTaskSchemaNodeData(sourceNode), 'Source node should be a task schema node');
  const mapFromStartIdx = sourceNode.sections.findIndex(
    (section) => section.sectionType === TaskSchemaNodeSectionType.MAP_FROM_START
  );
  assert(mapFromStartIdx >= 0, 'Expected a map from start section to exist', {sourceNode});
  const alreadyHasRow = sourceNode.sections[mapFromStartIdx].sectionRows.some(
    (row) => row.clientId === sourceFieldInfo.fieldClientId
  );
  if (alreadyHasRow) {
    return [];
  }

  const rootNode = staticFlowchart.nodesStaticData.find(
    (node): node is TaskSchemaStaticNodeData =>
      isStaticTaskSchemaNodeData(node) &&
      node.nodeDetailsType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS
  );
  assert(rootNode != null, 'Root node must exist', {staticFlowchart});
  const matchingRow = rootNode.sections
    .find((section) => section.sectionType === TaskSchemaNodeSectionType.INPUTS)
    ?.sectionRows.find((row) => row.clientId === sourceFieldInfo.fieldClientId);
  assert(
    matchingRow != null,
    'Expected to find a row in the root node corresponding to the edge source field',
    {rootNode, sourceNode, newEdge}
  );

  return [
    {
      type: FlowchartNodeChangeType.REPLACE,
      id: sourceNode.id,
      data: produce(sourceNode, (draftSourceNode) => {
        draftSourceNode.sections[mapFromStartIdx].sectionRows.push(
          convertRootRowToMapFromStartRow(matchingRow)
        );
      }),
    },
  ];
}

/**
 * Gets all node changes that must occur as a result of a new edge being added to the flowchart.
 *
 * These changes include:
 * - Updating the router config for router nodes that are affected by the new edge.
 * - Adding a row to the "map from start" section of the source node if it doesn't already exist.
 *
 * @param staticFlowchart The static flowchart the new edge is being added to.
 * @param newEdge The new edge that is being added.
 * @returns All node changes that must occur as a result of the new edge being added.
 */
export default function getNodeChangesForNewEdge(
  staticFlowchart: StaticTemplateFlowchart,
  newEdge: FlowchartStaticEdgeData
): Array<FlowchartNodeChange> {
  return [
    ...getRouterConfigChangesAfterEdgeChange(staticFlowchart, FlowchartEdgeChangeType.ADD, newEdge.id),
    ...getNodeChangesForNewEdgeFromMapFromStartSection(staticFlowchart, newEdge),
  ];
}
